import React, { useState } from "react";
import useSectionScroll from "../../hooks/useSectionScroll";

const faqs = [
  {
    q: "How much does a website or web application cost?",
    a: "Pricing depends on project scope, features, integrations, and timeline. After understanding your requirements, we provide a transparent proposal with no hidden costs.",
  },
  {
    q: "How long does it take to complete a project?",
    a: "Most business websites are delivered within 2–4 weeks, while custom web applications typically take 6–12 weeks depending on complexity.",
  },
  {
    q: "Which technologies do you use?",
    a: "We specialize in React, Next.js, Java, Spring Boot, Node.js, MongoDB, PostgreSQL, MySQL, Docker, AWS, and scalable cloud-native architectures.",
  },
  {
    q: "Do you provide support after launch?",
    a: "Yes. We offer maintenance, security updates, bug fixes, performance optimization, and continuous feature enhancements after deployment.",
  },
  {
    q: "Can you redesign an existing website?",
    a: "Absolutely. We modernize outdated websites, improve user experience, increase performance, and optimize them for SEO and conversions.",
  },
  {
    q: "Will my website be mobile-friendly?",
    a: "Yes. Every website and application we build is fully responsive, optimized for all devices, and tested across modern browsers.",
  },
];

export default function FAQ() {
  const [open, setOpen] = useState();
  const goToSection = useSectionScroll();

  return (
    <section id="faq" className="scroll-mt-24 relative overflow-hidden bg-white py-20">
      {/* Background Decorative Blur Gradients */}
      <div className="absolute -left-40 top-20 h-96 w-96 rounded-full bg-[#FBF0E2] blur-[120px] pointer-events-none" />
      <div className="absolute right-0 bottom-0 h-[450px] w-[450px] rounded-full bg-orange-100/40 blur-[140px] pointer-events-none" />

      <div className="relative mx-auto w-[92%] max-w-[1440px]">
        {/* Main Grid Content Area */}
        <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
          
          {/* Left Column: Compact Accordion System */}
          <div className="order-2 lg:order-none space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`group overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-500 ${
                  open === index
                    ? "border-[#C8762A] bg-[#FBF0E2] shadow-[0_15px_40px_rgba(200,118,42,.1)]"
                    : "border-stone-200 bg-white hover:border-[#E5C9A9] hover:shadow-md"
                }`}
              >
                <button
                  onClick={() => setOpen(open === index ? null : index)}
                  className="flex w-full items-center justify-between gap-4 p-5 text-left"
                >
                  <div className="flex items-start gap-4">
                    {/* CHANGED: Made number text smaller and crisp */}
                    <span className="text-2xl font-bold text-[#C8762A]/40 w-6">
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <div>
                      {/* CHANGED: Shrunk typography size from text-xl to text-base */}
                      <h3 className="text-base font-bold text-[#1A1714] leading-snug">
                        {faq.q}
                      </h3>
                    </div>
                  </div>

                  {/* CHANGED: Scaled icon container size from h-12/w-12 down to h-9/w-9 */}
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      open === index
                        ? "rotate-45 bg-[#C8762A] text-white"
                        : "bg-[#F5F5F5] text-[#C8762A] group-hover:bg-[#FBF0E2]"
                    }`}
                  >
                    <span className="material-symbols-outlined text-xl">
                      add
                    </span>
                  </div>
                </button>

                <div
                  className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    open === index ? "max-h-40" : "max-h-0"
                  }`}
                >
                  {/* CHANGED: Adjusted internal padding layout and shrunk down body spacing text to text-sm */}
                  <div className="pl-14 pr-5 pb-5">
                    <p className="text-sm leading-6 text-stone-600">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Right Column: Sticky Context Card */}
          <div className="order-1 lg:order-none sticky top-28 h-fit">
            <span className="inline-flex rounded-full bg-[#FBF0E2] px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C8762A]">
              FAQ
            </span>

            <h2 className="mt-5 text-4xl font-bold leading-tight text-[#1A1714]">
              Have
              <br />
              <span className="italic text-[#C8762A]">
                Questions?
              </span>
            </h2>

            <p className="mt-4 text-sm leading-6 text-stone-600">
              We've answered the most common questions about
              our design process, development workflow,
              pricing, timelines, and ongoing support.
            </p>

            <div className="mt-8 rounded-2xl border border-[#E7D9C5] bg-[#FBF0E2] p-5">
              <p className="text-xs uppercase tracking-widest text-[#C8762A]">
                Still Need Help?
              </p>
              
              <h4 className="mt-2 text-xl font-bold text-[#1A1714]">
                Let's Build Something Amazing
              </h4>
              
              <button
                onClick={() => goToSection("contact")}
                className="btn-dark mt-5 text-sm py-3 px-6 inline-block rounded-xl"
              >
                Start Your Project
              </button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}